import { motion } from "framer-motion";
import { Feather, Sparkles, BookOpen, Check, X } from "lucide-react";
import { IMAGES } from "@/context/AppContext";
import { NotifyForm } from "@/components/NotifyForm";
import { useNoIndex } from "@/hooks/useNoIndex";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}>
    {children}
  </motion.div>
);

const MOVEMENTS = [
  { n: "I", title: "Becoming Settled", body: "The life we build. The patterns we create. The way we protect ourselves. And the quiet longing beneath it all — the sense that there's something more." },
  { n: "II", title: "Being Tipped Over", body: "What happens when everything shifts. When circumstances, relationships, or faith push us out of our comfort. When staying the same is no longer possible. This is where the real invitation begins." },
  { n: "III", title: "Being Refilled", body: "The slower, quieter movement toward renewal. Not a quick fix or a transformation montage — the real kind, that requires stillness, honesty, and practice. The kind that makes us more ourselves, more alive to God's presence in everyday moments." },
];

const IS_NOT = [
  "A self-help book with three easy steps to a better life.",
  "Toxic positivity dressed up as spirituality.",
  "A story where suffering is beautiful or God is blamed for pain.",
  "Inspiration that leaves you feeling empty when real life is hard.",
];

const IS = [
  "An honest look at the gap between who we thought we were and who we're actually becoming.",
  "Real moments — interruptions, conversations, disappointments, quiet celebrations — where God showed up in ordinary life.",
  "An invitation to stop trying harder and start noticing more.",
  "A framework for understanding that transformation isn't about achieving a better version of yourself — it's about surrendering to the God who's already writing your story.",
];

const FOR = [
  "Women who've tried the self-help path and found it hollow.",
  "Mothers navigating the gap between expectation and reality.",
  "Wives discovering how faith actually works in marriage.",
  "Anyone who's felt life shift beneath them and wondered if God was still there.",
  "Anyone ready to ask: what if being poured out is exactly where I find myself?",
];

export default function Book() {
  useNoIndex();
  return (
    <div className="bg-[#F4EFE6] text-[#2A2825]">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal delay={0.1}>
          <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-5">The Memoir · Coming Fall 2026</p>
          <h1 className="font-serif-display font-light text-5xl sm:text-6xl leading-[1.02] mb-6">Reflections</h1>
          <p className="font-serif-display text-xl md:text-2xl text-[#5E4B3C] italic leading-snug mb-3">Stories, Poems &amp; Lessons on Being Tipped Over and Poured Out by God</p>
          <p className="tracking-overline text-xs uppercase text-[#8a7a6a] mb-8">By Dej B. Wikler</p>
          <p className="text-[#5E4B3C] leading-relaxed font-body mb-6 max-w-lg">A collection of personal stories, honest poems, and hard-won lessons about what happens when you stop trying to fix yourself and start noticing how God is already at work in ordinary life. It's about the moments that break you open and remake you.</p>
          <p className="font-serif-display text-xl md:text-2xl italic text-[#A9724E] leading-snug mb-10 max-w-lg">A story doesn't have to be dramatic to be useful. These are lessons on being tipped over and poured out by God.</p>
          <div>
            <p className="text-xs tracking-overline uppercase text-[#8a7a6a] mb-3">Be the first to know when it releases</p>
            <NotifyForm track="reflections-book" testid="notify-book" />
          </div>
        </Reveal>
        <Reveal>
          <div className="bg-[#E7DECF] flex items-center justify-center p-10 md:p-14">
            <img src={IMAGES.book} alt="Reflections book cover" className="max-h-[70vh] w-auto object-contain shadow-[0_30px_70px_-25px_rgba(42,40,37,0.5)]" data-testid="book-cover-image" />
          </div>
        </Reveal>
      </section>

      {/* HOOK */}
      <section className="py-24 md:py-32 bg-[#2A2825] text-[#F4EFE6] grain relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-10">What if the moment everything shifts is exactly where God meets you?</h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="space-y-5 text-[#C9BEB0] leading-relaxed font-body text-lg max-w-2xl mx-auto text-left">
              <p>For years, Dej believed transformation required discipline. More effort. Harder work. Trying to become someone different.</p>
              <p className="font-serif-display italic text-2xl text-[#E8B486]">Then life tipped her over.</p>
              <p>And in the disruption, she discovered something unexpected: God wasn't punishing her. He was inviting her. The patterns she'd built — the measured living, the performance, the smallness she'd settled for — were being emptied out. And in the emptying, there was space for something real.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE HEART OF IT */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-6 text-center">The heart of it</p>
            <blockquote className="font-serif-display font-light text-2xl md:text-3xl italic leading-snug text-center mb-3">
              "Moab has been at rest from youth, undisturbed… So I will send him pourers, and they shall pour him out; and they shall empty his vessels."
            </blockquote>
            <p className="text-center tracking-overline text-xs uppercase text-[#8a7a6a] mb-12">Jeremiah 48:11–12</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 text-[#5E4B3C] leading-relaxed font-body text-lg">
              <p>There's an invitation in being poured out. To rest, to be at peace, to feel safe — these aren't bad things. But what if you were made for more? What if the moments that shake you loose are actually love? What if being disrupted is God's way of saying, "There's more for you"?</p>
              <p>The dregs that settle at the bottom — old patterns, small beliefs, the comfort of staying the same — they're real. And God doesn't force them out. But when life shifts, when we're finally ready to grow, He meets us there. In the disruption, He shows us what we've been missing.</p>
              <p className="font-serif-display italic text-2xl text-[#A9724E]">Not through punishment. Through invitation.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THREE MOVEMENTS */}
      <section className="py-24 md:py-32 bg-[#EAE3D9]/50 border-y border-[#D8CEBE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4">Three movements</p>
          <h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-14 max-w-2xl">The shape of being remade.</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-[#D8CEBE] border border-[#D8CEBE]">
            {MOVEMENTS.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.1}>
                <div className="bg-[#F4EFE6] p-10 h-full">
                  <span className="font-serif-display text-4xl text-[#D8CEBE]">{m.n}</span>
                  <h3 className="font-serif-display text-2xl md:text-3xl mt-4 mb-3">{m.title}</h3>
                  <p className="text-[#5E4B3C] leading-relaxed font-body">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IS NOT / IS */}
      <section className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12">
          <Reveal>
            <p className="tracking-overline text-xs uppercase text-[#8a7a6a] mb-6">What this book is not</p>
            <ul className="space-y-4">
              {IS_NOT.map((t) => (
                <li key={t} className="flex items-start gap-3 text-[#5E4B3C] font-body leading-relaxed">
                  <X className="w-5 h-5 text-[#8a7a6a] shrink-0 mt-0.5" strokeWidth={1.5} />{t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-6">What this book is</p>
            <ul className="space-y-4">
              {IS.map((t) => (
                <li key={t} className="flex items-start gap-3 text-[#2A2825] font-body leading-relaxed">
                  <Check className="w-5 h-5 text-[#A9724E] shrink-0 mt-0.5" strokeWidth={1.5} />{t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-20 md:py-28 bg-[#EAE3D9]/50 border-y border-[#D8CEBE]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-8">Who this is for</p></Reveal>
          <div className="space-y-4">
            {FOR.map((t, i) => (
              <Reveal key={t} delay={i * 0.06}><p className="font-serif-display text-xl md:text-2xl font-light text-[#2A2825] leading-snug">{t}</p></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MEMOIR OF AWAKENING */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <div className="flex justify-center gap-6 mb-8 text-[#A9724E]">
              <Feather className="w-6 h-6" strokeWidth={1.25} /><Sparkles className="w-6 h-6" strokeWidth={1.25} /><BookOpen className="w-6 h-6" strokeWidth={1.25} />
            </div>
            <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-6">A memoir of awakening</p>
            <p className="text-[#5E4B3C] leading-relaxed font-body text-lg mb-6">Stories. Poems. Lessons. Honest reflections on what happens when you stop settling and start noticing. When you let yourself be disrupted. When you discover that the deepest transformation happens not through force, but through openness.</p>
            <p className="font-serif-display text-2xl md:text-3xl italic font-light text-[#2A2825] leading-snug">Before there was Outpoured, there were these stories — the ordinary moments where God's invitation became unmistakable.</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
